<script setup lang="ts">
import {
  EnableMaintenanceModeRequestDtoTypeEnum,
  type UpdateConnectionStringsRequestDtoMaintenanceModeRequestDto,
} from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import PtwCancelButton from '@/components/buttons/PtwCancelButton.vue';
import { SKIP_FORM_CHECK } from '@/components/confirmationModals/useFormConfirmationModal';
import DatabaseSettings from '@/components/DatabaseSettings/DatabaseSettings.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import { success } from '@/components/icons/icons.ts';
import MaintenanceModeDialog from '@/components/maintenanceMode/MaintenanceModeDialog.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { useSystemStore } from '@/stores/systemStore';
import { useDatabaseSettings } from '@/utils/useDatabaseSettings';
import { useMaintenanceModeDialog } from '@/utils/useMaintenanceModeDialog';
import { useCountdown } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { useIsFieldDirty } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { hasValidationErrors, isServerError } from '@/errorHandling';

const { t } = useTranslation();
const router = useRouter();
const notificationStore = useNotificationStore();
const { clearAllMessages } = notificationStore;

// Authentication store for logout countdown
const authenticationStore = useAuthenticationStore();

// Logout countdown state
const showRebootSuccessDialog = ref(false);
const countdownSeconds = ref(20);
const { remaining, start } = useCountdown(countdownSeconds, {
  async onComplete() {
    await router.push({ name: routenames.signOut, hash: `#${SKIP_FORM_CHECK}` });
  },
  onTick() {
    countdownSeconds.value = remaining.value;
  },
});

// Handle after reboot completion
async function handleAfterReboot() {
  // Check authentication status after reboot
  await authenticationStore.checkAuthentication();

  if (authenticationStore.isAuthenticated) {
    // User is still authenticated - redirect to management view with success message
    notificationStore.success(t('DatabaseConnected'));
    await router.push({ name: routenames.settings });
  } else {
    // User is no longer authenticated - show countdown dialog
    showRebootSuccessDialog.value = true;
    start();
  }
}

// Maintenance Mode Dialog
const {
  isOpen: isDialogOpen,
  openDialog,
  closeDialog,
  handleExceptionDetails,
  exceptionNotification,
  notificationMessage,
  loggedInUsers,
  errorHandler: maintenanceModeErrorHandler,
} = useMaintenanceModeDialog();

const hasSubmitServerError = ref(false);

const closeDialogOnValidationErrors = (error: Error) => {
  if (hasValidationErrors(error)) {
    closeMaintenanceModeDialog();
  }

  return error;
};

const trackServerErrorHandler = (error: Error) => {
  if (isServerError(error)) {
    hasSubmitServerError.value = true;
  }

  return error;
};

// Initial maintenance mode data structure
const initialMaintenanceModeRequestDto: UpdateConnectionStringsRequestDtoMaintenanceModeRequestDto =
  {
    password: '',
    ignoreLoggedInUsers: false,
    $type:
      EnableMaintenanceModeRequestDtoTypeEnum.PtwPtw100AppZeroApplicationSharedSystemComponentDtoEnableMaintenanceModeRequestDtoPtwPtw100AppZeroApplicationShared,
  };

// Use unified database settings composable
const {
  // Form
  meta,
  validate,
  handleSubmit,
  setFieldValue,
  form,

  // State
  loading,
  isCustomDatabaseUsed,
  isActive,
  isSubmitting,
  isRebooting,
  isWaitingForReboot,
  showSuccessMessage,
  showErrorMessage,
  defaultConnectionString,
  blobStoringConnectionString,
  isDefaultPasswordVisible,
  isBlobStoringPasswordVisible,

  // Methods
  handleDatabaseTypeChange,
  submitDatabaseSettings,
  toggleDefaultPasswordVisibility,
  toggleBlobStoringPasswordVisibility,
} = useDatabaseSettings({
  autoLoad: true, // Load installation config from API
  errorHandlers: [
    closeDialogOnValidationErrors,
    trackServerErrorHandler,
    handleExceptionDetails,
    maintenanceModeErrorHandler,
  ],
  afterReboot: handleAfterReboot,
});

// Dialog state management
function openMaintenanceModeDialog() {
  // Set initial maintenance mode data in the form
  setFieldValue('maintenanceModeRequestDto', initialMaintenanceModeRequestDto);
  openDialog();
}

function closeMaintenanceModeDialog() {
  // Clear maintenance mode data from the form
  setFieldValue('maintenanceModeRequestDto', null);
  closeDialog();
}
const showSelectionValidation = ref(false);
const databaseType = ref<'integrated' | 'custom'>('integrated');
const initialDatabaseType = ref<'integrated' | 'custom'>();

// Set the initial database type once when config loads
watch(loading, (loadingNew) => {
  if (
    !loadingNew &&
    isCustomDatabaseUsed.value !== undefined &&
    initialDatabaseType.value === undefined
  ) {
    const dbType = isCustomDatabaseUsed.value ? 'custom' : 'integrated';
    initialDatabaseType.value = dbType;
    databaseType.value = dbType;
  }
});

// Compare current selection vs. initial selection
const hasDatabaseSelectionChanged = computed(() => {
  if (initialDatabaseType.value === undefined) return false;
  return initialDatabaseType.value !== databaseType.value;
});

// Track individual connection string fields for dirty state using VeeValidate
const isDefaultServerDirty = useIsFieldDirty('defaultConnectionString.server');
const isDefaultPortDirty = useIsFieldDirty('defaultConnectionString.port');
const isDefaultDatabaseDirty = useIsFieldDirty('defaultConnectionString.database');
const isDefaultUserDirty = useIsFieldDirty('defaultConnectionString.user');
const isDefaultPasswordDirty = useIsFieldDirty('defaultConnectionString.password');
const isDefaultAdditionalParamsDirty = useIsFieldDirty(
  'defaultConnectionString.additionalParameters',
);

const isBlobServerDirty = useIsFieldDirty('blobStoringConnectionString.server');
const isBlobPortDirty = useIsFieldDirty('blobStoringConnectionString.port');
const isBlobDatabaseDirty = useIsFieldDirty('blobStoringConnectionString.database');
const isBlobUserDirty = useIsFieldDirty('blobStoringConnectionString.user');
const isBlobPasswordDirty = useIsFieldDirty('blobStoringConnectionString.password');
const isBlobAdditionalParamsDirty = useIsFieldDirty(
  'blobStoringConnectionString.additionalParameters',
);

// Custom dirty check for connection string fields
const isFormDirtyRelevant = computed(() => {
  // For integrated database: only the radio button matters (connection strings are read-only)
  if (databaseType.value === 'integrated') {
    return false;
  }

  // For a custom database: check if any connection string field is dirty
  return (
    isDefaultServerDirty.value ||
    isDefaultPortDirty.value ||
    isDefaultDatabaseDirty.value ||
    isDefaultUserDirty.value ||
    isDefaultPasswordDirty.value ||
    isDefaultAdditionalParamsDirty.value ||
    isBlobServerDirty.value ||
    isBlobPortDirty.value ||
    isBlobDatabaseDirty.value ||
    isBlobUserDirty.value ||
    isBlobPasswordDirty.value ||
    isBlobAdditionalParamsDirty.value
  );
});

// Combined change detection
const hasAnyChange = computed(() => hasDatabaseSelectionChanged.value || isFormDirtyRelevant.value);

// Hide selection validation when fields are modified
const allDirtyFields = [
  isDefaultServerDirty,
  isDefaultPortDirty,
  isDefaultDatabaseDirty,
  isDefaultUserDirty,
  isDefaultPasswordDirty,
  isDefaultAdditionalParamsDirty,
  isBlobServerDirty,
  isBlobPortDirty,
  isBlobDatabaseDirty,
  isBlobUserDirty,
  isBlobPasswordDirty,
  isBlobAdditionalParamsDirty,
];

watch(allDirtyFields, () => {
  if (showSelectionValidation.value && isFormDirtyRelevant.value) {
    showSelectionValidation.value = false;
  }
});

function handleDatabaseTypeChangeAndResetValidation(dbType: 'integrated' | 'custom') {
  showSelectionValidation.value = false;
  handleDatabaseTypeChange(dbType);
}

async function validateAndOpenMaintenanceModeDialog() {
  showSelectionValidation.value = false;

  // Show hint if no changes detected
  if (!hasAnyChange.value) {
    showSelectionValidation.value = true;
    return;
  }

  // Validate and open a dialog for any changes
  await validate();

  const isBackupConfirmed = !Object.keys(form.errorBag.value).some((path) =>
    path.startsWith('backupConfirmed'),
  );

  const isDatabaseSettingsFormValid =
    !Object.keys(form.errorBag.value).some((path) => path.startsWith('defaultConnectionString.')) &&
    !Object.keys(form.errorBag.value).some((path) =>
      path.startsWith('blobStoringConnectionString.'),
    );

  if (isBackupConfirmed && isDatabaseSettingsFormValid) {
    clearAllMessages();
    openMaintenanceModeDialog();
  }
}

async function handleDatabaseSuccess() {
  await useSystemStore().fetchMaintenanceModeInfo();
}

function handlePasswordVisibilityToggle(field: 'default' | 'blob') {
  if (field === 'default') toggleDefaultPasswordVisibility();
  if (field === 'blob') toggleBlobStoringPasswordVisibility();
}

// Form submission handler
const submit = handleSubmit(async (updateConnectionStringsRequestDto) => {
  hasSubmitServerError.value = false;
  const isSuccessful = await submitDatabaseSettings(
    updateConnectionStringsRequestDto,
    handleDatabaseSuccess,
  );

  const MAINTENANCE_DTO_PREFIX = 'maintenanceModeRequestDto.';
  const hasMaintenanceModeErrors = Object.keys(form.errorBag.value).some((path) =>
    path.startsWith(MAINTENANCE_DTO_PREFIX),
  );
  const shouldCloseAfterFailure =
    !hasMaintenanceModeErrors && !loggedInUsers.value && !hasSubmitServerError.value;

  // Success: always close dialog
  if (isSuccessful) {
    closeMaintenanceModeDialog();
    return;
  }

  // Failure: close only when errors belong to the form (not maintenance activation) and no exception details
  if (shouldCloseAfterFailure) {
    closeMaintenanceModeDialog();
  }
});
</script>

<template>
  <form @submit.prevent="submit">
    <BasePage>
      <template #title>
        <BasePageTitle :title="$t('DatabaseConfiguration')" />
      </template>

      <template #actions>
        <PtwCancelButton :to="{ name: routenames.settings }" />
        <Button
          class="ml-2"
          variant="primary"
          :loading="isSubmitting || isActive"
          :label="$t('SaveAndConnect')"
          @click="validateAndOpenMaintenanceModeDialog"
        />
      </template>

      <v-sheet class="pa-4">
        <DatabaseSettings
          v-model:databaseType="databaseType"
          :showSelectionValidation="showSelectionValidation"
          :isFirstTimeInstallation="authenticationStore.session?.isFirstTimeInstallation"
          :isCustomDatabaseUsed="isCustomDatabaseUsed"
          :defaultConnectionString="defaultConnectionString"
          :blobStoringConnectionString="blobStoringConnectionString"
          :isProcessing="isActive"
          :isRebooting="isRebooting"
          :isWaitingForReboot="isWaitingForReboot"
          :showSuccessMessage="showSuccessMessage"
          :showErrorMessage="showErrorMessage"
          :isDefaultPasswordVisible="isDefaultPasswordVisible"
          :isBlobStoringPasswordVisible="isBlobStoringPasswordVisible"
          @update:databaseType="handleDatabaseTypeChangeAndResetValidation"
          @update:passwordVisibility="
            (connectionStringFor) => handlePasswordVisibilityToggle(connectionStringFor)
          "
        />
      </v-sheet>
    </BasePage>

    <!-- Maintenance mode dialog -->
    <MaintenanceModeDialog
      :modelValue="isDialogOpen"
      :loading="isSubmitting"
      :exceptionNotification="exceptionNotification"
      :notificationMessage="notificationMessage"
      :loggedInUsers="loggedInUsers"
      :isValid="meta.valid"
      fieldNameContext="maintenanceModeRequestDto"
      @update:modelValue="(value) => !value && !isSubmitting && closeMaintenanceModeDialog()"
      @submit="submit"
      @cancel="closeMaintenanceModeDialog"
    />

    <!-- Reboot success dialog -->
    <Dialog :modelValue="showRebootSuccessDialog" hideClose>
      <template #title>
        {{ $t('SoftwareSettings.DatabaseChangeSuccessDialogTitle') }}
      </template>
      <template #text>
        <v-alert type="success" :icon="false">
          <template #text>
            <v-icon class="mr-2" :icon="success"></v-icon>
            <span class="font-weight-semibold">{{
              $t('SoftwareSettings.DatabaseSuccessfullyConnected')
            }}</span>
          </template>
        </v-alert>
        <div class="countdown my-8 text-center">
          <div class="counter font-weight-bold">
            {{ countdownSeconds }}
          </div>
          <p class="mt-3">
            {{ $t('SoftwareSettings.DatabaseChangeSuccessSignOutMsg') }}
          </p>
        </div>
      </template>
      <template #actions>
        <Button
          variant="primary"
          :label="$t('GoToSignInNow')"
          :to="{ name: routenames.signOut, hash: `#${SKIP_FORM_CHECK}` }"
        />
      </template>
    </Dialog>
  </form>
</template>

<style lang="scss" scoped>
.counter {
  font-size: 36px;
  line-height: 1.2;
}
</style>
